import React, { useState, useMemo, useRef, useEffect } from 'react';
import { detectGameType } from '../../utils/gameTypeUtils';
import { createGame } from '../../services/sessionService';
import Button from '../common/Button';
import PlayerSearchInput from '../common/PlayerSearchInput';
import {
  DEFAULT_SCORING,
  validateGameScore,
  shouldShowWinQuickButton,
  applyWinQuickScore,
} from '../../utils/gameScoreRules';
import { handleMobileScoreFocus, markSkipMobileScoreClear, mobileScoreInputAttrs } from '../../utils/mobileScoreInput';

/**
 * Log Game Form for Social Sessions
 * Allows any signed-up user to log a game they played in
 */
const LogGameForm = ({
  sessionId,
  clubId,
  seasonId,
  currentUserId,
  signedUpPlayers = [],
  playersById = {},
  onGameLogged,
  /** If true (session host, club host, or club admin), user can log games they did not play in */
  canLogWithoutPlaying = false,
  scoringConfig = DEFAULT_SCORING,
}) => {
  const [team1Players, setTeam1Players] = useState([null, null]);
  const [team2Players, setTeam2Players] = useState([null, null]);
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [scoreError, setScoreError] = useState(''); // Score validation error
  const team1ScoreInputRef = useRef(null);
  const team2ScoreInputRef = useRef(null);

  // Filter out already selected players from available players
  // excludeTeam and excludeIndex allow the current player being edited to remain available
  const getAvailablePlayers = (excludeTeam, excludeIndex) => {
    const selectedIds = new Set();
    
    // Add all selected players from both teams
    team1Players.forEach((p, idx) => {
      if (p && !(excludeTeam === 1 && idx === excludeIndex)) {
        selectedIds.add(p.id);
      }
    });
    team2Players.forEach((p, idx) => {
      if (p && !(excludeTeam === 2 && idx === excludeIndex)) {
        selectedIds.add(p.id);
      }
    });
    
    // Return only players that aren't already selected
    return signedUpPlayers.filter(p => !selectedIds.has(p.id));
  };

  // Auto-detect game type based on selected players
  const detectedGameType = useMemo(() => {
    const team1 = team1Players.filter(Boolean);
    const team2 = team2Players.filter(Boolean);
    if (team1.length !== 2 || team2.length !== 2) return null;
    
    return detectGameType(team1, team2);
  }, [team1Players, team2Players]);

  // Check if current user is in the game
  const isCurrentUserInGame = useMemo(() => {
    const allPlayers = [...team1Players, ...team2Players].filter(Boolean);
    return allPlayers.some(p => p?.id === currentUserId);
  }, [team1Players, team2Players, currentUserId]);

  const handleSelectPlayer = (teamIndex, playerIndex, player) => {
    if (teamIndex === 1) {
      const newTeam1 = [...team1Players];
      newTeam1[playerIndex] = player;
      setTeam1Players(newTeam1);
    } else {
      const newTeam2 = [...team2Players];
      newTeam2[playerIndex] = player;
      setTeam2Players(newTeam2);
    }
    setError(null);
  };

  const handleRemovePlayer = (teamIndex, playerIndex) => {
    if (teamIndex === 1) {
      const newTeam1 = [...team1Players];
      newTeam1[playerIndex] = null;
      setTeam1Players(newTeam1);
    } else {
      const newTeam2 = [...team2Players];
      newTeam2[playerIndex] = null;
      setTeam2Players(newTeam2);
    }
    setError(null);
  };

  const handleScoreChange = (team, value) => {
    // Only allow numeric input
    if (value === '' || /^\d+$/.test(value)) {
      if (team === 'team1') {
        setTeam1Score(value);
        // Auto-focus next input when 2 digits entered
        if (value.length === 2 && team2ScoreInputRef.current) {
          markSkipMobileScoreClear('team2');
          team2ScoreInputRef.current.focus();
        }
      } else {
        setTeam2Score(value);
      }
      setError(null);
      setScoreError(''); // Clear score error when user types
    }
  };

  const handleScoreIncrement = (team, delta) => {
    const currentScore = team === 'team1' ? team1Score : team2Score;
    const newScore = Math.max(0, Math.min(99, (parseInt(currentScore) || 0) + delta));
    const newScoreStr = newScore === 0 ? '' : newScore.toString();
    
    if (team === 'team1') {
      setTeam1Score(newScoreStr);
    } else {
      setTeam2Score(newScoreStr);
    }
    setError(null);
    setScoreError(''); // Clear score error when user changes score
  };

  const { pointsTarget } = scoringConfig;
  const showWinQuickButtons = shouldShowWinQuickButton(team1Score, team2Score, pointsTarget);
  const handleWinButton = (team) =>
    applyWinQuickScore(team, pointsTarget, {
      setTeam1Score,
      setTeam2Score,
      team1InputRef: team1ScoreInputRef,
      team2InputRef: team2ScoreInputRef,
      onClearErrors: () => {
        setError(null);
        setScoreError('');
      },
    });

  // Validate scores whenever they change
  useEffect(() => {
    if (team1Score !== '' && team2Score !== '') {
      const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
      setScoreError(validationError || '');
    } else {
      setScoreError('');
    }
  }, [team1Score, team2Score, scoringConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation: non-host/admin must include themselves in the game
    if (!canLogWithoutPlaying && !isCurrentUserInGame) {
      setError('You must include yourself in the game');
      return;
    }

    const allPlayers = [...team1Players, ...team2Players].filter(Boolean);
    if (allPlayers.length !== 4) {
      setError('Please select 4 players');
      return;
    }

    const score1 = parseInt(team1Score);
    const score2 = parseInt(team2Score);
    if (isNaN(score1) || isNaN(score2) || score1 < 0 || score2 < 0) {
      setError('Please enter valid scores');
      return;
    }

    if (score1 === score2) {
      setError('Scores cannot be equal');
      return;
    }

    // Validate badminton scoring rules
    const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
    if (validationError) {
      setError(validationError);
      setScoreError(validationError);
      return;
    }

    const winningTeam = score1 > score2 ? 1 : 2;

    setIsSubmitting(true);
    try {
      const gameData = {
        sessionId,
        clubId,
        season_id: seasonId,
        team1Players: team1Players.filter(Boolean),
        team2Players: team2Players.filter(Boolean),
        team_1_score: score1,
        team_2_score: score2,
        winningTeam,
        gameCreateMethod: 'self_reported',
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString()
      };

      await createGame(gameData, playersById);

      // Reset form
      setTeam1Players([null, null]);
      setTeam2Players([null, null]);
      setTeam1Score('');
      setTeam2Score('');

      if (onGameLogged) {
        onGameLogged();
      }
    } catch (err) {
      console.error('Error logging game:', err);
      setError(err.message || 'Failed to log game. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGameTypeDisplay = () => {
    if (!detectedGameType) return 'Select 4 players';
    return detectedGameType.name || 'Unknown';
  };

  return (
    <div className="bg-surface rounded-lg border border-border-medium shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-text-primary mb-2">Log a Game</h2>
        <p className="text-sm text-text-secondary">
          Record a game you played in. Select 4 players, enter scores, and submit.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Court-like Layout with Bordered Team Panels */}
        <div className="bg-surface rounded-lg border border-border-medium shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Team 1 Panel - Bordered like court UI */}
            <div className="p-4 rounded-md ring-1 flex flex-col min-w-0" style={{ backgroundColor: '#374151', borderColor: '#374151' }}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold text-white">Team 1</span>
              </div>
              <div className="flex flex-col gap-3">
                {[0, 1].map((index) => (
                  <div key={index}>
                    <label className="block text-xs font-medium text-white mb-1">
                      Player {index + 1}
                    </label>
                    <PlayerSearchInput
                      placeholder={`Select player ${index + 1}...`}
                      availablePlayers={getAvailablePlayers(1, index)}
                      selectedPlayer={team1Players[index]}
                      onSelectPlayer={(player) => handleSelectPlayer(1, index, player)}
                      onRemovePlayer={() => handleRemovePlayer(1, index)}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Team 2 Panel - Bordered like court UI */}
            <div className="p-4 rounded-md ring-1 flex flex-col min-w-0" style={{ backgroundColor: '#1E3A8A', borderColor: '#1E3A8A' }}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold text-white">Team 2</span>
              </div>
              <div className="flex flex-col gap-3">
                {[0, 1].map((index) => (
                  <div key={index}>
                    <label className="block text-xs font-medium text-white mb-1">
                      Player {index + 1}
                    </label>
                    <PlayerSearchInput
                      placeholder={`Select player ${index + 1}...`}
                      availablePlayers={getAvailablePlayers(2, index)}
                      selectedPlayer={team2Players[index]}
                      onSelectPlayer={(player) => handleSelectPlayer(2, index, player)}
                      onRemovePlayer={() => handleRemovePlayer(2, index)}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Score Input - Compact mobile-friendly design */}
          <div className="mt-4 w-full p-2 sm:p-3 rounded-lg border overflow-hidden" style={{ background: 'linear-gradient(to right, #374151, #1E3A8A)', borderColor: '#4B5563' }}>
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              {/* Team 1 Score Section */}
              <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                <span className="text-xs font-medium text-white mb-0.5">Team 1</span>
                <div className="flex items-center gap-0.5 sm:gap-1">
                  {/* Decrement Button - Hidden on mobile */}
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team1', -1)}
                    className="hidden sm:flex w-8 h-8 items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md border border-white/30 font-bold text-lg transition-colors"
                    aria-label="Decrease Team 1 score"
                  >
                    −
                  </button>
                  
                  {/* Score Input - Smaller on mobile */}
                  <input
                    ref={team1ScoreInputRef}
                    placeholder="0"
                    value={team1Score}
                    onChange={(e) => handleScoreChange('team1', e.target.value)}
                    onFocus={(e) => handleMobileScoreFocus(e, 'team1', () => setTeam1Score(''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !error && team1Score && team2Score) {
                        handleSubmit(e);
                      }
                    }}
                    maxLength={2}
                    className="w-14 sm:w-16 h-8 sm:h-8 px-2 border-2 border-white/50 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white text-center text-base sm:text-lg font-bold text-gray-900"
                    {...mobileScoreInputAttrs}
                  />
                  
                  {/* Increment Button - Hidden on mobile */}
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team1', 1)}
                    className="hidden sm:flex w-8 h-8 items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md border border-white/30 font-bold text-lg transition-colors"
                    aria-label="Increase Team 1 score"
                  >
                    +
                  </button>
                </div>
                
                {showWinQuickButtons && (
                <button
                  type="button"
                  onClick={() => handleWinButton('team1')}
                  className="px-2 sm:px-4 py-1 sm:py-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors shadow-sm border border-white/30 mt-0.5"
                  aria-label={`Set Team 1 score to ${pointsTarget}`}
                >
                  Win ({pointsTarget})
                </button>
                )}
              </div>
              
              {/* VS Divider - Compact on mobile */}
              <div className="flex flex-col items-center gap-0.5 flex-shrink-0 px-1">
                <div className="w-0.5 h-4 sm:h-6 bg-white/50"></div>
                <span className="text-xs sm:text-sm font-bold text-white whitespace-nowrap">VS</span>
                <div className="w-0.5 h-4 sm:h-6 bg-white/50"></div>
              </div>
              
              {/* Team 2 Score Section */}
              <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                <span className="text-xs font-medium text-white mb-0.5">Team 2</span>
                <div className="flex items-center gap-0.5 sm:gap-1">
                  {/* Decrement Button - Hidden on mobile */}
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team2', -1)}
                    className="hidden sm:flex w-8 h-8 items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md border border-white/30 font-bold text-lg transition-colors"
                    aria-label="Decrease Team 2 score"
                  >
                    −
                  </button>
                  
                  {/* Score Input - Smaller on mobile */}
                  <input
                    ref={team2ScoreInputRef}
                    placeholder="0"
                    value={team2Score}
                    onChange={(e) => handleScoreChange('team2', e.target.value)}
                    onFocus={(e) => handleMobileScoreFocus(e, 'team2', () => setTeam2Score(''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !error && team1Score && team2Score) {
                        handleSubmit(e);
                      }
                    }}
                    maxLength={2}
                    className="w-14 sm:w-16 h-8 sm:h-8 px-2 border-2 border-white/50 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white text-center text-base sm:text-lg font-bold text-gray-900"
                    {...mobileScoreInputAttrs}
                  />
                  
                  {/* Increment Button - Hidden on mobile */}
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team2', 1)}
                    className="hidden sm:flex w-8 h-8 items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-md border border-white/30 font-bold text-lg transition-colors"
                    aria-label="Increase Team 2 score"
                  >
                    +
                  </button>
                </div>
                
                {showWinQuickButtons && (
                <button
                  type="button"
                  onClick={() => handleWinButton('team2')}
                  className="px-2 sm:px-4 py-1 sm:py-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors shadow-sm border border-white/30 mt-0.5"
                  aria-label={`Set Team 2 score to ${pointsTarget}`}
                >
                  Win ({pointsTarget})
                </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Score Validation Error */}
          {scoreError && (
            <div className="mt-2 bg-danger-light border border-danger/30 text-danger-dark px-3 py-2 rounded text-sm text-center">
              {scoreError}
            </div>
          )}
        </div>

        {/* Game Type Display */}
        {detectedGameType && (
          <div className="bg-info-light border border-border-medium rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">Game Type</p>
                <p className="text-lg font-semibold text-primary">{getGameTypeDisplay()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-danger-light border border-danger/30 rounded-lg p-3">
            <p className="text-sm text-danger-dark">{error}</p>
          </div>
        )}

        {/* Warning if current user not in game (hosts/admins can log without playing) */}
        {!canLogWithoutPlaying && !isCurrentUserInGame && team1Players.some(Boolean) && team2Players.some(Boolean) && (
          <div className="bg-warning-light border border-warning/40 rounded-lg p-3">
            <p className="text-sm text-warning-dark">
              ⚠️ You must include yourself in the game to log it.
            </p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={isSubmitting || (!isCurrentUserInGame && !canLogWithoutPlaying) || !detectedGameType || !!scoreError}
          className="w-full"
        >
          {isSubmitting ? 'Logging Game...' : 'Log Game'}
        </Button>
      </form>
    </div>
  );
};

export default LogGameForm;

